import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardAlerts, type DashboardAlert } from "@/components/dashboard/DashboardAlerts";
import { api } from "@/lib/apiClient";

interface OnlineBookingReadinessIssue {
  code: string;
  severity: "error" | "warning";
  title: string;
  detail: string;
  action_path: string;
  action_label: string;
}

interface OnlineBookingReadiness {
  blocks_booking_flow: boolean;
  issues: OnlineBookingReadinessIssue[];
}

function toDashboardAlerts(issues: OnlineBookingReadinessIssue[]): DashboardAlert[] {
  return issues.map((issue) => ({
    id: `online-booking-${issue.code}`,
    tone: issue.severity === "error" ? "danger" : "warning",
    title: issue.title,
    detail: issue.detail,
    href: issue.action_path,
    hrefLabel: issue.action_label,
  }));
}

export function OnlineBookingReadinessAlerts({
  placement,
  reloadToken = 0,
}: {
  placement: "dashboard" | "settings";
  reloadToken?: number;
}) {
  const [readiness, setReadiness] = useState<OnlineBookingReadiness | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<OnlineBookingReadiness>("/api/v1/settings/online-booking-readiness")
      .then((data) => {
        if (!cancelled) setReadiness(data);
      })
      .catch(() => {
        if (!cancelled) setReadiness(null);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  if (!readiness || readiness.issues.length === 0) return null;

  if (placement === "dashboard") {
    return <DashboardAlerts alerts={toDashboardAlerts(readiness.issues)} />;
  }

  const blocked = readiness.blocks_booking_flow;
  return (
    <section
      className={
        blocked
          ? "rounded-xl border border-status-red/35 bg-status-red/10 px-4 py-4"
          : "rounded-xl border border-status-orange/40 bg-status-orange/10 px-4 py-4"
      }
      role="region"
      aria-label="Hinweise zur Online-Buchung"
    >
      <h2 className="text-sm font-semibold text-label">
        {blocked
          ? "Die Buchungsstrecke funktioniert mit dieser Konfiguration nicht"
          : "Einige Wege in der Buchungsstrecke sind noch unvollständig"}
      </h2>
      <p className="mt-1 text-sm text-label-secondary">
        {blocked
          ? "Gäste bleiben sonst in der Buchung hängen, zum Beispiel bei „Platz wählen“, ohne eine Fehlermeldung zu sehen. Stellen Sie die folgenden Punkte ein."
          : "Einzelne Anreisearten oder Kategorien erscheinen in der Buchungsstrecke nicht. Die offenen Punkte:"}
      </p>
      <ul className="mt-3 space-y-3">
        {readiness.issues.map((issue) => (
          <li
            key={issue.code}
            className="rounded-lg border border-separator bg-card/80 px-3 py-3"
          >
            <p className="text-sm font-medium text-label">{issue.title}</p>
            <p className="mt-1 text-sm text-label-secondary">{issue.detail}</p>
            <Link
              to={issue.action_path}
              className="mt-2 inline-block text-sm font-medium text-accent hover:underline"
            >
              {issue.action_label}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
